import { ContextReactiveOptions } from "./reactive-abstraction-types";
import { isObject } from "./reactive-utils";

type Context = { reactiveOptions?: ContextReactiveOptions };

export const simplePrototypeList = new WeakSet<object>();

/**
 * Registers a prototype object to be considered "simple".
 *
 * Objects whose prototype is in this list will be treated by `isSimpleObject`
 * as candidates for deep reactivity by default. This allows custom classes or
 * objects with specific prototypes to be automatically wrapped in reactive proxies
 * when encountered within a larger reactive structure, assuming `deep` reactivity
 * is enabled.
 *
 * @param simplePrototype The prototype object to register.
 */
export const addSimplePrototype = (simplePrototype: object) => {
	simplePrototypeList.add(simplePrototype);
};

/**
 * Unregisters a prototype object, so it's no longer considered "simple".
 *
 * This is the counterpart to `addSimplePrototype`. Objects whose prototype was
 * previously registered will no longer be identified as "simple" by default
 * by `isSimpleObject` after being removed.
 *
 * @param simplePrototype The prototype object to unregister.
 */
export const removeSimplePrototype = (simplePrototype: object) => {
	simplePrototypeList.delete(simplePrototype);
};

export const isSimpleObject = <T>(object: T): object is T & object => {
	return (
		Array.isArray(object) ||
		(isObject(object) &&
			(object instanceof Set ||
				object instanceof Map ||
				object instanceof Promise ||
				Object.getPrototypeOf(object) === Object.prototype ||
				Object.getPrototypeOf(object) === null ||
				simplePrototypeList.has(Object.getPrototypeOf(object))))
	);
};

export const getChildOptions = (
	parentContext: Context
): ContextReactiveOptions => {
	return { plugins: parentContext.reactiveOptions?.plugins };
};

export const getChild = <T>(
	context: Context,
	object: T,
	createProxy: () => T
): T => {
	if (context.reactiveOptions?.deep === false || !isSimpleObject(object)) {
		return object;
	} else {
		return createProxy();
	}
};
